import { getTenantByHost } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { AnimatePresence } from 'framer-motion';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useCustomerThemeQuery } from '../hooks/tenant/useCustomerTheme';
import { applyThemeVars, ensureFontLink } from '../utils/applyThemeVars';
import { ThemeLoader } from './ThemeLoader';

// Every built-in fallback - `var()` defaults, assets, copy - is Pelicargo's, so any other tenant
// would flash Peli branding while its theme is in flight. The tenant comes from the hostname and
// cannot change at runtime, so this is resolved once.
const SHOULD_HOLD_FOR_THEME = !getTenantByHost()?.isSystemTenant;

// The loader stays at least this long once up - long enough for its logo to finish fading in, and
// so a fast theme doesn't blink it on and straight off again.
const MIN_VISIBLE_MS = 1500;

type Props = {
  children: ReactNode;
};

/**
 * Pushes the tenant's theme overrides onto `<html>` as CSS custom properties, and holds non-Peli
 * tenants behind a tenant-branded loader until the theme has settled.
 *
 * `ChakraProvider` sits above `<TRPC>` in `main.tsx` and its theme object is built at module-eval
 * time, so fetched overrides cannot reach the theme. They are applied as custom properties instead,
 * which the Chakra tokens already reference via `var(--peli-*, <static value>)`. They are written
 * in a layout effect, so they are on `<html>` before any frame that shows the real tree.
 *
 * For a non-Peli tenant the sequence is: blank page -> the loader fades in -> the theme settles and
 * the loader holds out its minimum -> the app mounts underneath as the loader fades out. The app is
 * not mounted any earlier, or it would show through the loader while that is still fading in.
 *
 * The loader is one-shot: once it has left, nothing brings it back - a later refetch must never
 * unmount the app. A failed fetch opens the gate too: the static tenant fallbacks are better than a
 * stuck loader.
 */
export const ThemeGate = ({ children }: Props) => {
  const { data: theme, status } = useCustomerThemeQuery();
  const showLoader = useLoaderVisibility(SHOULD_HOLD_FOR_THEME, status === 'loading');
  const showApp = !showLoader;

  useLayoutEffect(() => {
    applyThemeVars(theme);
  }, [theme]);

  useEffect(() => {
    ensureFontLink(theme?.fonts?.url);
  }, [theme?.fonts?.url]);

  return (
    <>
      <Show if={showApp}>{children}</Show>
      <AnimatePresence>{showLoader ? <ThemeLoader key="theme-loader" /> : null}</AnimatePresence>
    </>
  );
};

/**
 * Whether the loader is up: from mount when `isEnabled`, until `isLoading` clears and the loader has
 * been up for `MIN_VISIBLE_MS`. Latches off - it never comes back once it has gone.
 */
const useLoaderVisibility = (isEnabled: boolean, isLoading: boolean) => {
  const [isVisible, setIsVisible] = useState(isEnabled);
  const shownAt = useRef(Date.now());

  useEffect(() => {
    if (!isVisible || isLoading) return undefined;

    const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt.current);
    const timer = setTimeout(() => setIsVisible(false), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [isLoading, isVisible]);

  return isVisible;
};
